'use client';

import { useState } from 'react';
import Image from 'next/image';
import { Play } from 'lucide-react';
import { useLocale } from 'next-intl';
import { useTrack } from '@owlcat/app-shell';

/**
 * A YouTube facade: the poster is our own image and the iframe is only mounted
 * once someone presses play. Embedding the player on load would pull in
 * YouTube's scripts and cookies for every visitor, including those who never
 * watch — and those who declined tracking. nocookie + no autoplay preview keeps
 * the landing light and the consent banner honest.
 *
 * It has to LOOK like a video — a tester scrolled past it three times on a phone
 * because a still with a small text pill reads as one more picture. So the whole
 * poster is the button and a big round play triangle sits in the middle.
 *
 * What it must NOT do is bury the poster: the poster is now a thumbnail that
 * carries its own hook and its own words (scripts/demo-video/thumbnails.mjs), so
 * a heavy veil and a caption pill on top of it would hide the very thing that
 * earns the press. A whisper of dim behind the button, the length in the corner,
 * nothing else.
 */
export function VideoFrame({
  videoId,
  poster,
  soonLabel,
  playLabel,
  lengthLabel,
  vertical = false,
  className,
}: {
  videoId: string | null;
  poster: string;
  soonLabel: string;
  playLabel: string;
  /** e.g. "Видео · 1:30" — shown on the poster so the block reads as a film */
  lengthLabel?: string;
  /** A 9:16 cut (the Shorts edit) — the frame follows the film's shape. */
  vertical?: boolean;
  /** Which breakpoints this frame is shown at (the phone gets the vertical one). */
  className?: string;
}) {
  const [playing, setPlaying] = useState(false);
  const locale = useLocale();
  const track = useTrack();
  const play = () => {
    setPlaying(true);
    track('landing_demo_played', { app_id: 'portal', surface: 'video', language: locale });
  };
  /**
   * Ours, or YouTube's?
   *
   * This used to accept only a path beginning with "/", so a film on our own
   * media CDN — an absolute https URL, which is how every campaign publishes
   * now — was taken for a YouTube id and embedded as
   * youtube-nocookie.com/embed/https://… : a dead frame where the sample film
   * should be. Anything with a scheme or a slash is a file of ours; a YouTube
   * id is a bare token.
   */
  const local = !!videoId && (videoId.startsWith('/') || /^https?:\/\//.test(videoId));

  return (
    <div className={`relative overflow-hidden rounded-3xl border bg-muted/30 shadow-xl ${className ?? ''}`}>
      <div className={`relative ${vertical ? 'aspect-[9/16]' : 'aspect-video'}`}>
        {playing && videoId && local ? (
          <video
            className="absolute inset-0 h-full w-full bg-black"
            src={videoId}
            poster={poster}
            controls
            autoPlay
            playsInline
            data-testid="landing-video-file"
          />
        ) : playing && videoId ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`}
            title={playLabel}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="landing-video-iframe"
          />
        ) : (
          <>
            {/* OUR picture, never YouTube's auto-thumbnail: that one is a frame of
                the film, i.e. a screenshot of the app, and a screenshot among
                screenshots is what made a tester scroll past the video three times.
                One big friendly drawing and a play button — nothing to read. */}
            {/* A poster that lives on the media CDN is served as it is: the
                optimizer only knows the hosts the server was STARTED with, and
                a campaign whose artwork moved to the bucket after that boot
                came back as an empty frame with a play button on it. Our own
                public/ files keep the optimizer. */}
            <Image
              src={poster}
              alt=""
              fill
              priority
              unoptimized={/^https?:\/\//.test(poster)}
              sizes="(max-width: 1024px) 100vw, 960px"
              className="object-cover"
            />
            {/* Just enough to keep the white triangle readable over any artwork. */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
            {videoId ? (
              <button
                type="button"
                onClick={play}
                aria-label={playLabel}
                /* Bottom-left, not the middle: the middle of this poster is where
                   the photo → hero pair lives, and a button on top of it hides the
                   one thing that makes anyone press it. */
                className="group absolute inset-0 flex cursor-pointer items-end justify-start p-3 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/60 sm:p-4"
                data-testid="landing-video-play"
              >
                <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary/95 shadow-2xl transition group-hover:scale-105 sm:h-16 sm:w-16">
                  <span className="absolute inset-0 animate-ping rounded-full bg-primary/30" aria-hidden />
                  {/* the triangle sits a hair right of centre — optical centring */}
                  <Play className="relative h-7 w-7 translate-x-0.5 fill-white text-white sm:h-9 sm:w-9" aria-hidden />
                </span>
              </button>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex items-center gap-3 rounded-full bg-background/85 px-5 py-3 shadow-lg backdrop-blur">
                  <Play className="h-5 w-5 text-primary" aria-hidden />
                  <span className="text-sm font-semibold">{soonLabel}</span>
                </div>
              </div>
            )}
            {videoId && lengthLabel && (
              <span className="pointer-events-none absolute bottom-3 right-3 rounded-md bg-black/75 px-2 py-1 text-xs font-medium text-white">
                {lengthLabel}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
